import React, {SyntheticEvent, useState, useEffect, useRef} from 'react';
import {IconButton} from '@mui/material'
import clsx from 'clsx'

import {StyledLoanCalculatorSlider} from './LoanCalculatorSliderStyled'
import {monthsAmountDisplayedNames} from '../../../common/helpers/helpers'
import {
    ILoanCalculatorEditableProps,
    ILoanCalculatorOffertSummaryProps,
    LoanCalculatorSlidersProps,
    LoanCalculatorSlidersTypes
} from './LoanOfferSummaryPanelEditableProps'
import {getStepValueSorted, numberToDisplayString} from '../../../common/helpers/number.helpers'
import AppButton from '../../atoms/AppButton/AppButton'
import {UI_BUTTON_TYPE} from '../../atoms/AppButton/AppButtonProps'
import PageHeader from '../../atoms/PageHeader/PageHeader'
import {HEADER_LEVEL} from '../../atoms/PageHeader/PageHeaderProps'
import {APP_COLOR} from '../../atoms/AtomsGlobalProps'
import AddIcon from '../../../icons/svg/add-icon.svg'
import MinusIcon from '../../../icons/svg/minus-icon.svg'
import {usePositiveNumberState} from "../../../pages/CustomClientOfferPage/hooks/usePositiveNumberState";
import {useSelector} from "react-redux";
import {AppState} from "../../../store";
import OverlayLoader from "../../../icons/OverlayLoader/OverlayLoader";
import LoanCalculatorManualChooseInstalment from './LoanCalculatorManualChooseInstalment/LoanCalculatorManualChooseInstalment'


import './LoanOfferSummaryPanelEditableStyles.scss'


const LoanCalculatorSliders = (props: LoanCalculatorSlidersProps) => {
    const {steps, min, max, type, headerText, currentSliderValue, onLoanValueSliderUpdate, onValueChanged} = props
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const DEFAULT_MIN_DEBOUNCE_TIMEOUT = 250
    const DEFAULT_MAX_DEBOUNCE_TIMEOUT = 1000
    const DEFAULT_STEP_DEBOUNCE_TIMEOUT = 150
    const [isDebouncing, setIsDebouncing] = useState(false);
    const isLimitsEqual = min === max

    const [sliderValue, setSliderValue] = usePositiveNumberState(currentSliderValue, min);
    const snappedRef = useRef<number>(sliderValue);
    const lastRawRef = useRef<number>(sliderValue);

    const [stepUp, setStepUp] = useState<number>(() => {
        return getStepValueSorted(props.steps, sliderValue);
    })

    const [stepDown, setStepDown] = useState<number>(() => {
        return getStepValueSorted(props.steps, sliderValue - stepUp);
    })

    const marks = [
        {value: min, label: numberToDisplayString(min)},
        {value: max, label: numberToDisplayString(max)},
    ]

    useEffect(() => {
        setSliderValue(currentSliderValue)
    }, [ currentSliderValue ])

    const getSliderHeadingText = () => {
        switch (type) {
            case LoanCalculatorSlidersTypes.amount: {
                return numberToDisplayString(sliderValue, " zł")
            }
            case LoanCalculatorSlidersTypes.period: {
                return monthsAmountDisplayedNames(sliderValue)
            }

            default: {
                return ''
            }
        }
    }
    useEffect(() => {
        let stepUp = getStepValueSorted(steps, sliderValue);
        if (sliderValue + stepUp > max) {
            stepUp = (max - sliderValue)
        }
        let stepDown = getStepValueSorted(steps, sliderValue - stepUp);
        if (sliderValue === max) {
            if (sliderValue % stepDown) {
                stepDown = sliderValue % stepDown;
            }
        }
        else if (sliderValue - stepDown < min) {
            stepDown = (sliderValue - min)
        }

        setStepUp(stepUp);
        setStepDown(stepDown);

    }, [sliderValue])

    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current)
                setIsDebouncing(false)
            }
        }
    }, [])

    const noOfRecalculation = useSelector(
        (state: AppState) => state.offertState.noOfRecalculation
    );

    const handleOnButtonClick = async (actionType: 'add' | 'subtract') => {
        onValueChanged()
        let newSliderValue = 0
        if (actionType === 'add') {
            newSliderValue = sliderValue + stepUp
        }
        else if (actionType === 'subtract') {
            newSliderValue = sliderValue - stepDown
        }

        if (newSliderValue < min) {
            newSliderValue = min;
        }
        else if (newSliderValue > max) {
            newSliderValue = max;
        }
        else {
            setSliderValue(newSliderValue)
        }
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current)
        }
        const delay = Math.min(
            DEFAULT_MAX_DEBOUNCE_TIMEOUT,
            DEFAULT_MIN_DEBOUNCE_TIMEOUT + (noOfRecalculation - 1) * DEFAULT_STEP_DEBOUNCE_TIMEOUT
        );
        setIsDebouncing(true)


        debounceTimeoutRef.current = setTimeout(async () => {
            await onLoanValueSliderUpdate(newSliderValue, type)
            setIsDebouncing(false)
        }, delay)
    }

    const clamp = (v: number) => Math.min(Number(max), Math.max(Number(min), v));
    useEffect(() => {
        snappedRef.current = sliderValue;
        lastRawRef.current = sliderValue;
    }, [sliderValue]);

    const getSliderMarks = () => {
        switch (type) {
            case LoanCalculatorSlidersTypes.amount: {
                return [
                    {value: min, label: numberToDisplayString(min, ' zł')},
                    {value: max, label: numberToDisplayString(max, ' zł')},
                ]
            }
            case LoanCalculatorSlidersTypes.period: {
                return [
                    {value: min, label: numberToDisplayString(min, ' mies.')},
                    {value: max, label: numberToDisplayString(max, ' mies.')},
                ]
            }
        }
    }
    const handleChange = (_: any, newValue: number | number[]) => {
        onValueChanged()
        const raw = Array.isArray(newValue) ? newValue[0] : newValue;

        const minValue = Number(min);
        const maxValue = Number(max);

        const safeStepUp = Math.max(1, Number(stepUp));
        const safeStepDown = Math.max(1, Number(stepDown));

        const lastRaw = lastRawRef.current;
        const dirUp = raw > lastRaw;

        let snapped = snappedRef.current;

        const alignUp = (v: number) => {
            if (v <= minValue) {
                return minValue;
            }
            if (v >= maxValue) {
                return maxValue;
            }
            const aligned = Math.ceil(v / safeStepUp) * safeStepUp;
            return clamp(aligned);
        };

        const alignDown = (v: number) => {
            if (v <= minValue) {
                return minValue;
            }
            if (v >= maxValue) {
                return maxValue;
            }
            const aligned = Math.floor(v / safeStepDown) * safeStepDown;
            return clamp(aligned);
        };

        if (dirUp) {
            snapped = alignUp(snapped);
        }
        else {
            snapped = alignDown(snapped);
        }

        const stepDir = dirUp ? safeStepUp : safeStepDown;

        if (dirUp) {
            const k = Math.floor((raw - snapped) / stepDir);
            if (k >= 1) {
                snapped = snapped + k * stepDir;
            }
        }
        else {
            const k = Math.floor((snapped - raw) / stepDir);
            if (k >= 1) {
                snapped = snapped - k * stepDir;
            }
        }

        if (raw >= max) {
            snapped = max;
        }
        if (raw <= min) {
            snapped = min;
        }

        snapped = clamp(snapped);
        lastRawRef.current = raw;

        const final = snapToFinalStepWithDir(snapped, dirUp);

        if (final !== snappedRef.current) {
            snappedRef.current = snapped;
            setSliderValue(final);
        }
    };

    const snapToFinalStepWithDir = (v: number, dirUp: boolean) => {
        const clampedValue = clamp(v);
        if (clampedValue === Number(min) || clampedValue === Number(max)) {
            return clampedValue;
        }

        const base = Number(min);
        const stepFinal = getStepValueSorted(steps, clampedValue);
        if (!stepFinal || stepFinal <= 0) {
            return clampedValue;
        }

        const stepIndex = (clampedValue - base) / stepFinal;
        const snappedIndex = dirUp ? Math.ceil(stepIndex) : Math.floor(stepIndex);

        return clamp(base + snappedIndex * stepFinal);
    };

    const handleCommitted = () => {
        if (!Array.isArray(sliderValue)) {
            onLoanValueSliderUpdate(sliderValue, type)
        }
    };


   return (
      <div>
         <PageHeader title={headerText} level={HEADER_LEVEL.H4} aria-live="polite" color={APP_COLOR.PRIMARY} align='center'
            style={{marginBottom: 0}}/>
         <div className='loan-calculator-editable-slider-wrapper'>
            <IconButton
               type='button'
               aria-controls={type === 'amount' ? 'loan-slider-amount' : 'loan-slider-period'}
               aria-label={type === 'amount' ? "Zmniejsz kwotę pożyczki" : "Skróć okres spłaty pożyczki"}
               disabled={isLimitsEqual}
               id={type === LoanCalculatorSlidersTypes.amount? 'loan-calculator-slider-button-subtract-amount' : 'loan-calculator-slider-button-subtract-period'}
               style={{gridArea: 'subtract-button'}} className="loan-calculator-editable-slider-button"
               onClick={() => handleOnButtonClick('subtract')}
            >
               <img src={MinusIcon} alt="" aria-hidden="true" style={{ width: 15 }} />
            </IconButton>
            {/* todo*/}
            <PageHeader title={getSliderHeadingText()} level={HEADER_LEVEL.H4} aria-live="polite" color={APP_COLOR.SECONDARY} align='center'
                        style={{marginBottom: '0.25rem'}}
            />
            <IconButton
               type='button'
               aria-label={type === 'amount' ? "Zwiększ kwotę pożyczki" : "Wydłuż okres spłaty pożyczki"}
               disabled={isLimitsEqual}
               id={type === LoanCalculatorSlidersTypes.amount ? 'loan-calculator-slider-button-add-amount' : 'loan-calculator-slider-button-add-period'}
               style={{gridArea: 'add-button'}} className="loan-calculator-editable-slider-button"
               onClick={() => handleOnButtonClick('add')}
           >
            <img src={AddIcon} alt="" aria-hidden="true" style={{width: 15}}
 className='loan-calculator-slider-icon'/>
            </IconButton>
            <StyledLoanCalculatorSlider
               id={type === 'amount' ? 'loan-slider-amount' : 'loan-slider-period'}
               aria-label={type === 'amount' ? "Kwota pożyczki" : "Okres spłaty"}
               aria-valuemin={min}
               aria-valuemax={max}
               aria-valuenow={sliderValue}
               aria-valuetext={getSliderHeadingText()}
               style={{gridArea: 'slider'}}
               value={sliderValue}
               min={min}
               max={max}
               step={Math.max(1, Math.min(stepUp, stepDown))}
               marks={getSliderMarks()}
               onChange={handleChange}
               onChangeCommitted={handleCommitted}
               className={clsx(
                  sliderValue === max && 'loan-calculator-slider-right-half',
                  sliderValue === min && 'loan-calculator-slider-left-half',
                  isLimitsEqual && 'loan-calculator-slider-centered-mark'
               )}
            />
      </div>
    </div>
)
}


const LoanCalculatorOfferSummary = (props: ILoanCalculatorOffertSummaryProps) => {

  const {offert, isLoading} = props

  return (
  <div className={'loan-summary-details'}>
     <OverlayLoader show={isLoading} />
     <dl className='loan-summary-wrapper'>
        <div className='loan-summary' style={{ gridArea: 'requestedAmount'}}>
           <dt>Kwota</dt>
           <dd>
              {numberToDisplayString(offert?.requestedAmount, " zł", true)}
           </dd>
        </div>
        <div className='loan-summary' style={{ gridArea: 'instalments'}}>
           <dt>Okres spłaty</dt>
           <dd>
              {monthsAmountDisplayedNames(offert.instalments)}
           </dd>
        </div>
        <div className='loan-summary' style={{ gridArea: 'instalmentsAmount'}}>
           <dt>Kwota raty</dt>
           <dd>
              {numberToDisplayString(offert.instalmentAmount, " zł", true)}
           </dd>
        </div>
        <div className='loan-summary' style={{ gridArea: 'interestRate'}}>
           <dt>Oprocentowanie</dt>
           <dd>
              {numberToDisplayString(offert.interestRate, "%")}
           </dd>
        </div>
        <div className='loan-summary' style={{ gridArea: 'rrso'}}>
           <dt>RRSO</dt>
           <dd>
              {numberToDisplayString(offert.rrso, "%",)}
           </dd>
        </div>
        <div className='loan-summary' style={{ gridArea: 'ckdz'}}>
           <dt>Całkowita kwota do spłaty</dt>
           <dd>
              {numberToDisplayString(offert.ckdz, " zł")}
           </dd>
        </div>
     </dl>
  </div>
  )
}

const LoanOfferSummaryPanelEditable = (props: ILoanCalculatorEditableProps) => {
    const { handleOnClose, loanCalculatorStateParams, onLoanValueSliderUpdate, isLoading, onValueChanged, handleOnPredefinedPeriodButtonClick } = props
    const { IS_PREDEFINED_PERIODS_AVAILABLE } = useSelector((state: AppState) => state.applicationState)

    return (
     <section className='loan-calculator-outer-wrapper' aria-labelledby="loan-calculator-editable-title">
         <h2 id="loan-calculator-editable-title" className='loan-calculator-editable-label--visually-hidden'>
            Panel zmiany parametrów pożyczki: wybierz kwotę i okres spłaty.
         </h2>
         <div className='loan-calculator-editable-inner-wrapper'>
              <LoanCalculatorSliders
                  type={LoanCalculatorSlidersTypes.amount}
                  steps={loanCalculatorStateParams.requestedAmountSteps}
                  min={loanCalculatorStateParams.minLoanAmount}
                  max={loanCalculatorStateParams.maxLoanAmount}
                  currentSliderValue={loanCalculatorStateParams.requestedAmount}
                  headerText={"Chcę pożyczyć"}
                  onLoanValueSliderUpdate={onLoanValueSliderUpdate}
                  onValueChanged={onValueChanged}
              />
              {
                IS_PREDEFINED_PERIODS_AVAILABLE === true ? // [ZAŁOŻENIE BIZNESOWE] - MECHANIZM PREDEFINIOWANYCH OKRESÓW POŻYCZKI
                (
                    <LoanCalculatorManualChooseInstalment 
                        instalmentAmount={loanCalculatorStateParams.instalmentAmount} 
                        handleOnPredefinedPeriodButtonClick={ handleOnPredefinedPeriodButtonClick }
                    />
                ) : 
                (
                    <LoanCalculatorSliders
                        type={LoanCalculatorSlidersTypes.period}
                        steps= { [ {from: 0, value: 1}  ] }
                        min={loanCalculatorStateParams.minLoanPeriod}
                        max={loanCalculatorStateParams.maxLoanPeriod}
                        currentSliderValue={loanCalculatorStateParams.instalments}
                        headerText={"Na okres"}
                        onLoanValueSliderUpdate={onLoanValueSliderUpdate}
                        onValueChanged={onValueChanged}
                    />
                )
              }
              <LoanCalculatorOfferSummary
                offert={loanCalculatorStateParams}
                isLoading={isLoading}
               />
               <AppButton
                  uiButtonType={UI_BUTTON_TYPE.SECONDARY_DARKEN}
                  onClick={handleOnClose}
                  className='loan-calculator-editable-slider-submit-button'>
                  Potwierdzam
              </AppButton>
           </div>
     </section>
  )
}

export default LoanOfferSummaryPanelEditable